import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Settings } from '@/types';

export default function SettingsPage() {
  const { isDark, settings, updateSettings } = useApp();
  const [form, setForm] = useState<Settings>({ ...settings });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const inp = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-700'}`;
  const lbl = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`;
  const card = `rounded-xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-2xl">
      <div><h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Settings</h2><p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure your business preferences and fee rates</p></div>

      <div className={card}>
        <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Business Info</h3>
        <div className="space-y-4">
          <div><label className={lbl}>Business Name</label><input value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} className={inp} /></div>
          <div><label className={lbl}>Currency</label>
            <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className={`${inp} cursor-pointer`}>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </div>
        </div>
      </div>

      <div className={card}>
        <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Money Transfer Fee Rates</h3>
        <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>These rates apply to all new transfers. Existing transfers keep their original rates.</p>
        <div className="space-y-4">
          <div>
            <label className={lbl}>My Commission Rate (% per 100 {form.currency})</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="100" step="0.1" value={form.myCommissionRate} onChange={e => setForm(f => ({ ...f, myCommissionRate: parseFloat(e.target.value) || 0 }))} className={`${inp} max-w-xs`} />
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>%</span>
            </div>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>On a {form.currency} 1,000 transfer: {form.currency} {(1000 * form.myCommissionRate / 100).toFixed(2)} commission</p>
          </div>
          <div>
            <label className={lbl}>Service Provider Commission Rate (% per 100 {form.currency})</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="100" step="0.1" value={form.providerCommissionRate} onChange={e => setForm(f => ({ ...f, providerCommissionRate: parseFloat(e.target.value) || 0 }))} className={`${inp} max-w-xs`} />
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>%</span>
            </div>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>On a {form.currency} 1,000 transfer: {form.currency} {(1000 * form.providerCommissionRate / 100).toFixed(2)} provider cost</p>
          </div>
          <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Net Profit per 100 {form.currency}</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{form.currency} {((form.myCommissionRate - form.providerCommissionRate)).toFixed(2)}</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>= {form.myCommissionRate}% - {form.providerCommissionRate}% = {(form.myCommissionRate - form.providerCommissionRate).toFixed(2)}%</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">Save Settings</button>
        {saved && <span className="flex items-center gap-1.5 text-sm text-emerald-500"><i className="ri-check-line" /> Settings saved!</span>}
      </div>
    </div>
  );
}
